<template>
    <div v-if="shouldShowIndicator" class="multistep-indicator">
        <div class="step-navigation">
            <template
                v-for="(step, index) in availableSteps"
                :key="index"
            >
                <div
                    @click="handleStepClick(step, index)"
                    :class="[
                        'bubble',
                        'clickable',
                        { 
                            filled: (index + 1) <= currentStepIndex,
                            disabled: !isStepAccessible(index)
                        }
                    ]"
                >
                    {{ index + 1 }}
                </div>
                <div
                    v-if="index !== availableSteps.length - 1"
                    class="line"
                    ref="line"
                >
                    <div
                        class="fill"
                        :style="{
                            width:
                                (index + 1) < currentStepIndex
                                    ? '100%'
                                    : '0%',
                        }"
                    ></div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import EventBus from "../event-bus";
import stepNavigationMixin from "../mixins/stepNavigationMixin";
import { safeGetJson } from "../utils/safeStorage";

export default {
    name: "SmartStepIndicator",
    mixins: [stepNavigationMixin],
    inject: {
        pageSettings: {
            default: () => ({})
        }
    },
    data() {
        return {
        };
    },
    props: {
        showExplanation: {
            type: Boolean,
            default: true,
        },
        step1Heading: {
            type: String,
            default: "Schritt 1",
        },
        step1Description: {
            type: String,
            default: "Erstelle deine Geschichte",
        },
        step2Heading: {
            type: String,
            default: "Schritt 2",
        },
        step2Description: {
            type: String,
            default: "Personalisiere dein Buch",
        },
        step3Heading: {
            type: String,
            default: "Schritt 3",
        },
        step3Description: {
            type: String,
            default: "Bestelle dein Buch",
        },
    },
    computed: {
        currentStep() {
            return this.$route.path.split("/").pop();
        },
        currentStepIndex() {
            const step = this.currentStep;
            if (step === 'step1') return 1;
            if (step === 'step2') return 2;
            if (step === 'step3') return 3;
            return 0;
        },
        shouldShowIndicator() {
            const currentStep = this.currentStep;
            const isStepPage = ["step1", "step2", "step3"].includes(currentStep);
            const hasRealPageSettings = this.pageSettings && Object.keys(this.pageSettings).length > 0;
            const hasSteps = this.availableSteps.length > 0;
            
            
            return isStepPage && hasRealPageSettings && hasSteps;
        },
        availableSteps() {
            const steps = [];
            const pageSettings = this.pageSettings || {};
            
            
            if (pageSettings.step1Url) {
                steps.push({
                    heading: this.step1Heading,
                    description: this.step1Description,
                    link: pageSettings.step1Url,
                });
            }
            
            if (pageSettings.step2Url) {
                steps.push({
                    heading: this.step2Heading,
                    description: this.step2Description,
                    link: pageSettings.step2Url,
                });
            }
            
            if (pageSettings.step3Url) {
                steps.push({
                    heading: this.step3Heading,
                    description: this.step3Description,
                    link: pageSettings.step3Url,
                });
            }
            
            
            return steps;
        },
    },
    methods: {
        isStepAccessible(stepIndex) {
            if (stepIndex === 0) return true;
            
            
            for (let i = 0; i < stepIndex; i++) {
                const stepName = this.getStepNameByIndex(i);
                const storageKey = this.getStorageKeyForStep(this.pageSettings, stepName);
                
                if (!storageKey) {
                    continue;
                }
                
                const stepValues = safeGetJson(storageKey, {});
                const hasEmptyRequiredFields = this.checkValues(stepValues);
                const hasNoValues = Object.keys(stepValues).length === 0;
                
                if (hasEmptyRequiredFields || hasNoValues) {
                    return false;
                }
            }
            
            return true;
        },
        
        handleStepClick(step, index) {
            if (!this.isStepAccessible(index)) {
                const firstMissingStep = this.findFirstMissingStep();
                if (firstMissingStep) {
                    EventBus.emit("throwErrorForRequiredInputs", true);
                    
                    this.$router.push(this.getUrlForStep(this.pageSettings, firstMissingStep));
                    
                    this.$nextTick(() => {
                        this.scrollToFirstEmptyField();
                    });
                } else {
                    EventBus.emit("throwErrorForRequiredInputs", true);
                    
                    this.scrollToFirstEmptyField();
                }
                return;
            }
            
            this.$router.push(step.link);
        },
        
        findFirstMissingStep() {
            const stepOrder = ['StepOne', 'StepTwo', 'StepThree'];
            
            for (let i = 0; i < stepOrder.length; i++) {
                const stepName = stepOrder[i];
                const storageKey = this.getStorageKeyForStep(this.pageSettings, stepName);
                
                if (!storageKey) continue;
                
                const stepValues = safeGetJson(storageKey, {});
                const hasEmptyRequiredFields = this.checkValues(stepValues);
                const hasNoValues = Object.keys(stepValues).length === 0;
                
                if (hasEmptyRequiredFields || hasNoValues) {
                    return stepName;
                }
            }
            
            return null;
        },
        
        getStepNameByIndex(index) {
            const stepNames = ['StepOne', 'StepTwo', 'StepThree'];
            return stepNames[index] || '';
        },
        
        setFirstLineFillWidth(args) {
            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    if (!this.$refs.line || this.$refs.line.length === 0) {
                        return;
                    }

                    const fillElement =
                        this.$refs.line[0].querySelector(".fill");
                    if (!fillElement) {
                        return;
                    }

                    const currentTab = args[0];
                    const tabLength = args[1];

                    if (currentTab === 0) {
                        fillElement.style.width = "0%";
                    } else {
                        const fillWidth = (currentTab / tabLength) * 100;
                        fillElement.style.width = `${fillWidth}%`;
                    }
                });
            });
        },
    },
    mounted() {
        EventBus.on("tabChanged", this.setFirstLineFillWidth);
    },
    beforeUnmount() {
        EventBus.off("tabChanged", this.setFirstLineFillWidth);
    },
};
</script>

<style lang="scss" scoped>
@use "@/assets/customizer.mixins.scss" as *;
.multistep-indicator {
    display: flex;
    flex-direction: column;
    align-items: center;
    row-gap: 24px;
    width: 100%;


    .step-navigation {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        max-width: 520px;

        @media (max-width: $breakpoint-mobile) {
            column-gap: 8px;
            padding: 0 16px;
        }

        a {
            display: block;
            text-decoration: none;
        }

        .bubble {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            width: 40px !important;
            height: 40px !important;
            min-width: 40px !important;
            min-height: 40px !important;
            max-width: 40px !important;
            max-height: 40px !important;
            padding: 0 !important;
            border-radius: 50% !important;
            background-color: $color-normal-grey !important;
            color: $color-new-black !important;
            font-size: 18px !important;
            font-weight: 700 !important;
            line-height: 1 !important;
            transition: background-color 0.3s, opacity 0.3s, cursor 0.3s !important;
            flex-shrink: 0 !important;
            box-sizing: border-box !important;

            &.filled {
                background-color: $color-new-black !important;
                color: $color-white !important;
            }

            &.clickable {
                cursor: pointer !important;
                
                &:hover:not(.disabled) {
                    background-color: $color-primary-dt-blue !important;
                    color: $color-white !important;
                }
            }

            &.disabled {
                opacity: 0.5 !important;
                cursor: not-allowed !important;
                
                &:hover {
                    background-color: $color-normal-grey !important;
                    color: $color-new-black !important;
                }
            }
        }

        .line {
            position: relative;
            width: 100%;
            max-width: 156px;
            height: 5px;
            border-radius: 40px;
            background-color: $color-normal-grey;
            overflow: hidden;

            .fill {
                position: absolute;
                top: 0;
                left: 0;
                display: block;
                width: 0;
                height: 100%;
                background-color: $color-new-black;
                transition: width 0.3s;
            }
        }
    }
}
</style>
